/**
 * 할로윈 2026 띠배너 장식 — 반짝이는 별 · 창문 불빛 (#6061 H5).
 *
 * 디자인 정본: Figma `Halloween 2026 — Strip Banner` (`31437:7443`) 「Spec · 움직임」 표 · 값과 로직은 디자인
 * 핸드오프 패키지(리포 밖)의 설정 · 띠배너 장식 스크립트를 옮겼다.
 *
 * ```
 *   별        19px 당 1개(모바일 15) · 18% 는 4갈래 반짝이 · 별마다 1.8–5초 주기 · 진하기 .85
 *   창문 불빛  0.9–3.5초마다 창 하나가 0.42–0.8초 동안 촛불처럼 어두워졌다 돌아옴(계단식 4단)
 *   reduced-motion  별 · 달무리는 CSS 가 멈추고(globals.css), 창문은 예약하지 않는다
 * ```
 *
 * 별은 **마운트 뒤에** 그린다 — 위치가 무작위라 서버 렌더에 넣으면 hydration 이 어긋난다.
 */
export const HALLOWEEN_2026_STRIP = {
  stars: {
    spacing: { desktop: 19, mobile: 15 },
    twinkle: [1.8, 5] as const,
    sparkleRatio: 0.18,
  },
  windowFlicker: {
    interval: [900, 3500] as const,
    duration: [420, 800] as const,
  },
  breakpoint: 1024,
};

/** 별 표식 — 테스트 · 디버그에서 찾는다 */
export const STRIP_STAR_ATTR = "data-hw26-star";

const rand = (a: number, b: number) => a + Math.random() * (b - a);

const SPARKLE =
  '<svg viewBox="0 0 10 10" width="100%" height="100%" aria-hidden="true" focusable="false"><path d="M5 0l1 4 4 1-4 1-1 4-1-4-4-1 4-1z" fill="currentColor"/></svg>';

/**
 * 별을 `sky` 에 채운다 — 블루 노이즈 배치(후보 8개 중 기존 별과 가장 먼 곳) · 별마다 다른 주기 · 지연 · 밝기.
 * 바깥 = 위치 · 최대 밝기(정적), 안쪽 core = 반짝임(opacity · transform 만).
 */
export function renderStrip2026Stars(
  sky: HTMLElement,
  mobile: boolean,
  cfg = HALLOWEEN_2026_STRIP,
): number {
  const W = sky.clientWidth || 1200;
  const H = sky.clientHeight || 40;
  const n = Math.round(W / cfg.stars.spacing[mobile ? "mobile" : "desktop"]);
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i < n; i++) {
    let best = { x: 0, y: 0 };
    let bd = -1;
    for (let k = 0; k < 8; k++) {
      const x = Math.random() * W;
      const y = 3 + Math.random() * (H - 10);
      let d = Infinity;
      for (const p of pts)
        d = Math.min(d, Math.hypot((p.x - x) / 1.6, p.y - y));
      if (d > bd) {
        bd = d;
        best = { x, y };
      }
    }
    pts.push(best);
  }
  sky.innerHTML = pts
    .map((p) => {
      const big = Math.random() < cfg.stars.sparkleRatio;
      const s = big ? 5 + Math.random() * 3 : 1 + Math.random() * 1.6;
      const dur = rand(...cfg.stars.twinkle).toFixed(2);
      const delay = (-Math.random() * 5).toFixed(2);
      const hi = (0.55 + Math.random() * 0.45).toFixed(2);
      return `<i ${STRIP_STAR_ATTR} class="hw26-star${big ? " hw26-star--sparkle" : ""}" style="left:${p.x.toFixed(1)}px;top:${p.y.toFixed(1)}px;width:${s.toFixed(1)}px;height:${s.toFixed(1)}px;--hw26-twinkle:${dur}s;--hw26-twinkle-delay:${delay}s;--hw26-star-hi:${hi}"><i class="hw26-star-core">${big ? SPARKLE : ""}</i></i>`;
    })
    .join("");
  return n;
}

/**
 * 띠배너 장식을 돌린다 — 별을 채우고 창문 불빛을 예약한다. 돌려준 함수로 멈춘다.
 *
 * - 폭이 바뀔 때만 별을 다시 그린다(200ms 디바운스).
 * - reduced-motion 이면 창문을 예약하지 않고, 도중에 켜지면 멈춘다.
 */
export function mountStrip2026Deco(
  deco: HTMLElement,
  cfg = HALLOWEEN_2026_STRIP,
): () => void {
  const sky = deco.querySelector<HTMLElement>("[data-hw26-sky]");
  if (!sky || typeof window.matchMedia !== "function") return () => {};
  const rm = window.matchMedia("(prefers-reduced-motion: reduce)");

  let width = 0;
  let resizeT: ReturnType<typeof setTimeout> | undefined;
  let flickerT: ReturnType<typeof setTimeout> | undefined;

  const render = () => {
    width = deco.clientWidth;
    renderStrip2026Stars(sky, width < cfg.breakpoint, cfg);
  };

  const { interval, duration } = cfg.windowFlicker;
  const flicker = () => {
    const w = deco.querySelectorAll<SVGElement>(".hw26-win");
    if (w.length) {
      w[Math.floor(Math.random() * w.length)].animate?.(
        [
          { opacity: 1 },
          { opacity: 0.25 },
          { opacity: 0.9 },
          { opacity: 0.4 },
          { opacity: 1 },
        ],
        { duration: rand(...duration), easing: "steps(4, end)" },
      );
    }
    flickerT = setTimeout(flicker, rand(...interval));
  };
  const startFlicker = () => {
    clearTimeout(flickerT);
    if (!rm.matches) flickerT = setTimeout(flicker, rand(...interval));
  };
  const stopFlicker = () => {
    clearTimeout(flickerT);
    flickerT = undefined;
    deco
      .querySelectorAll(".hw26-win")
      .forEach((e) => e.getAnimations?.().forEach((a) => a.cancel()));
  };
  const onRM = () => (rm.matches ? stopFlicker() : startFlicker());

  const ro =
    typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(() => {
          if (deco.clientWidth === width) return;
          clearTimeout(resizeT);
          resizeT = setTimeout(render, 200);
        });

  render();
  ro?.observe(deco);
  rm.addEventListener?.("change", onRM);
  startFlicker();

  return () => {
    rm.removeEventListener?.("change", onRM);
    ro?.disconnect();
    clearTimeout(resizeT);
    stopFlicker();
    sky.replaceChildren();
  };
}
